import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, Loader2, RefreshCw, X, Wand2, UserPlus, Check, XCircle,
  ExternalLink, AlertTriangle, Send, Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { plagiarismApi } from '@/lib/api';

/** Per-chapter plagiarism drawer (iter-14).
 *
 *  Pulls the most recent scan + open suggestions, gives the author one button
 *  to kick off a new scan (deducts tokens server-side), and renders each match
 *  as a card with a toolbar:
 *    [AI rewrite] [Assign to editor ▼] [Accept] [Decline]
 *
 *  Once the author accepts an AI-or-editor rewrite the server snapshots the
 *  current chapter, replaces the flagged span, and bumps version history.
 */
export function PlagiarismDrawer({ open, onClose, project, chapter, isOwner, canMutate, onChapterChanged }) {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [editors, setEditors] = useState([]);
  const [busyIdx, setBusyIdx] = useState(null);
  // iter-16 — provider selector + per-user wallet balances.
  const [providers, setProviders] = useState([]);
  const [activeProvider, setActiveProvider] = useState('');
  // Polling for pending scans (provider webhook may take a few seconds).
  const isPending = scan?.status === 'pending';

  const reload = useCallback(async () => {
    if (!open || !project?.id || !chapter?.id) return;
    setLoading(true);
    try {
      const [latest, ed, prov] = await Promise.all([
        plagiarismApi.latest(project.id, chapter.id),
        plagiarismApi.listEditors(project.id).catch(() => ({ editors: [] })),
        plagiarismApi.listProviders().catch(() => ({ providers: [], default: '' })),
      ]);
      setScan(latest.scan || null);
      setSuggestions(latest.suggestions || []);
      setEditors(ed.editors || []);
      setProviders(prov.providers || []);
      // Default to the admin's preferred provider; fall back to the first
      // configured one in the list.
      setActiveProvider((prev) => prev || prov.default || (prov.providers?.[0]?.id ?? ''));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load plagiarism state.');
    } finally { setLoading(false); }
  }, [open, project?.id, chapter?.id]);

  useEffect(() => { reload(); }, [reload]);

  // Poll every 3s while pending so the UI updates without a hard refresh.
  useEffect(() => {
    if (!open || !isPending) return undefined;
    const t = setInterval(reload, 3000);
    return () => clearInterval(t);
  }, [open, isPending, reload]);

  const runScan = async () => {
    if (!canMutate) { toast.warning('Read-only members cannot run plagiarism scans.'); return; }
    setScanning(true);
    try {
      const r = await plagiarismApi.scan(project.id, chapter.id, {
        sandbox: false,
        provider: activeProvider || undefined,
      });
      const walletLabel = r.wallet === 'copyscape_tokens' ? 'Copyscape tokens' : 'tokens';
      if (r.status === 'completed') {
        toast.success(`Scan complete (cost: ${r.token_cost} ${walletLabel}, ${r.matches?.length || 0} matches).`);
      } else {
        toast.success(`Scan queued (cost: ${r.token_cost} ${walletLabel}). Results in a few seconds…`);
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the scan.');
    } finally { setScanning(false); }
  };

  const createSug = async (matchIdx, action, editorId = null) => {
    setBusyIdx(matchIdx);
    try {
      const r = await plagiarismApi.createSuggestion(scan.id, matchIdx, action, editorId);
      toast.success(action === 'rewrite' ? 'AI rewrite drafted — review below.' : 'Assigned to editor.');
      setSuggestions((s) => [...s, r.suggestion]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suggestion failed.');
    } finally { setBusyIdx(null); }
  };

  const acceptSug = async (sid) => {
    try {
      await plagiarismApi.accept(sid);
      toast.success('Applied — chapter updated and version snapshotted.');
      onChapterChanged?.();
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not accept.'); }
  };
  const declineSug = async (sid) => {
    try {
      await plagiarismApi.decline(sid);
      toast.message('Suggestion declined.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not decline.'); }
  };

  if (!open) return null;

  return (
    <aside
      data-testid="plagiarism-drawer"
      className="absolute right-0 top-0 z-30 h-full w-[420px] flex flex-col border-l border-panel-border bg-panel shadow-xl"
    >
      <PlagiarismHeader
        scan={scan}
        onClose={onClose}
        onReload={reload}
        onScan={runScan}
        scanning={scanning}
        canMutate={canMutate}
        providers={providers}
        activeProvider={activeProvider}
        setActiveProvider={setActiveProvider}
      />

      <div className="flex-1 overflow-auto scroll-thin">
        {loading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="ml-2 text-[13.5px]">Loading…</span>
          </div>
        )}

        {!loading && !scan && (
          <EmptyPlagiarism canMutate={canMutate} onScan={runScan} scanning={scanning} />
        )}

        {!loading && scan && (
          <PlagiarismBody
            scan={scan}
            suggestions={suggestions}
            editors={editors}
            isOwner={isOwner}
            canMutate={canMutate}
            busyIdx={busyIdx}
            onRewrite={(idx) => createSug(idx, 'rewrite')}
            onAssign={(idx, editorId) => createSug(idx, 'assign', editorId)}
            onAccept={acceptSug}
            onDecline={declineSug}
            onChapterChanged={onChapterChanged}
            reload={reload}
          />
        )}
      </div>
    </aside>
  );
}

// ────────────────────────────────────────────────────────────────────

function PlagiarismHeader({ scan, onClose, onReload, onScan, scanning, canMutate,
                            providers, activeProvider, setActiveProvider }) {
  const current = providers.find((p) => p.id === activeProvider);
  return (
    <div className="border-b border-panel-border bg-panel-header/60">
      {/* Feb-2026 · Split the header into a title row + status row so
       * the score / match / provider labels can no longer collide with
       * the Reload / Scan / Close buttons on narrow drawers. */}
      <div className="flex items-center gap-2 px-3 py-2">
        <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
        <div className="flex-1 min-w-0 text-[14px] font-semibold">Plagiarism check</div>
        <Button variant="ghost" size="sm" onClick={onReload}
                className="h-7 w-7 shrink-0 p-0 shadow-none hover:shadow-none"
                data-testid="plagiarism-reload">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onScan}
          disabled={scanning || !canMutate || !providers.length}
          className="h-7 shrink-0 gap-1.5 px-2 text-[13px] text-primary shadow-none hover:shadow-none"
          data-testid="plagiarism-run-scan"
          title={!canMutate ? 'Read-only role cannot run scans.' : !providers.length ? 'No provider configured by admin.' : 'Run a new scan'}
        >
          {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          Scan
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}
                className="h-7 w-7 shrink-0 p-0 shadow-none hover:shadow-none"
                data-testid="plagiarism-close">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Status row — one line per scan state, wraps below the title
       * row so nothing overlaps the toolbar buttons above. */}
      {scan && (
        <div className="border-t border-border/50 bg-panel-header/40 px-3 py-1.5 text-[13px] text-muted-foreground"
             data-testid="plagiarism-status-row">
          {scan.status === 'pending' && (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              Provider processing — auto-refresh every 3 s.
            </span>
          )}
          {scan.status === 'completed' && (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Score <span className="font-mono-stat text-primary">{(scan.score ?? 0).toFixed(1)}%</span></span>
              <span>·</span>
              <span>{(scan.matches?.length || 0)} matches</span>
              <span>·</span>
              <span>provider <span className="font-mono-stat">{scan.provider}</span></span>
            </span>
          )}
          {!['pending', 'completed'].includes(scan.status) && (
            <span className="text-destructive">
              Status: {scan.status}{scan.error ? ` — ${scan.error}` : ''}
            </span>
          )}
        </div>
      )}
      {!scan && (
        <div className="border-t border-border/50 bg-panel-header/40 px-3 py-1.5 text-[13px] text-muted-foreground">
          No scan yet for this chapter.
        </div>
      )}

      {/* iter-16 — provider picker + wallet balance on its own row. */}
      {providers.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border/50 px-3 py-1.5 text-[13px]">
          {providers.length > 1 ? (
            <Select value={activeProvider} onValueChange={setActiveProvider}>
              <SelectTrigger className="h-6 w-[160px] text-[13px]" data-testid="plagiarism-provider-picker">
                <SelectValue placeholder="Pick provider" />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`plagiarism-provider-option-${p.id}`}>
                    {p.name} {p.is_sync ? '· sync' : '· async'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="font-mono-stat text-primary" data-testid="plagiarism-provider-fixed">
              {current?.name || providers[0]?.name}
            </span>
          )}
          {current && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-0.5 font-mono-stat text-[13px]" data-testid="plagiarism-wallet-pill">
              <Wallet className="h-3 w-3 text-muted-foreground" />
              {current.balance.toLocaleString()} {current.wallet === 'copyscape_tokens' ? 'CS' : 'tok'}
              <span className="text-muted-foreground">· cost {current.cost}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyPlagiarism({ onScan, scanning, canMutate }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <ShieldCheck className="h-8 w-8 text-muted-foreground/50" />
      <p className="text-[14px] font-medium">No scan has been run yet.</p>
      <p className="text-[13px] text-muted-foreground max-w-[260px]">
        Plagiarism scanning compares this chapter against the public web and the provider's
        academic corpus. Tokens are deducted from your wallet on submission.
      </p>
      <Button
        size="sm"
        onClick={onScan}
        disabled={scanning || !canMutate}
        className="h-8 gap-1.5"
        data-testid="plagiarism-empty-scan"
      >
        {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        Run plagiarism scan
      </Button>
    </div>
  );
}

function PlagiarismBody({ scan, suggestions, editors, isOwner, canMutate, busyIdx,
                          onRewrite, onAssign, onAccept, onDecline, onChapterChanged, reload }) {
  const sugsByMatch = useMemo(() => {
    const m = {};
    for (const s of suggestions) (m[s.match_index] = m[s.match_index] || []).push(s);
    return m;
  }, [suggestions]);

  if (scan.status !== 'completed') {
    return (
      <div className="p-4 text-[13.5px] text-muted-foreground">
        {scan.status === 'pending' ? (
          <><Loader2 className="inline h-3 w-3 animate-spin" /> Waiting for the provider to finish scanning…</>
        ) : (
          <span className="text-destructive">
            <AlertTriangle className="inline h-3 w-3" /> Scan ended with status <code>{scan.status}</code>.
          </span>
        )}
      </div>
    );
  }

  if (!scan.matches?.length) {
    return (
      <div className="p-6 text-center">
        <Check className="mx-auto h-6 w-6 text-success" />
        <p className="mt-2 text-[14px] font-medium">No matches detected.</p>
        <p className="text-[13px] text-muted-foreground">Chapter scored <span className="font-mono-stat">{(scan.score ?? 0).toFixed(1)}%</span> against the corpus.</p>
      </div>
    );
  }

  return (
    <div className="p-2 space-y-2.5">
      {scan.matches.map((m, idx) => (
        <MatchCard
          key={m.url || m.signature || `match-${idx}`}
          idx={idx}
          match={m}
          suggestions={sugsByMatch[idx] || []}
          editors={editors}
          isOwner={isOwner}
          canMutate={canMutate}
          busy={busyIdx === idx}
          onRewrite={onRewrite}
          onAssign={onAssign}
          onAccept={onAccept}
          onDecline={onDecline}
          reload={reload}
          onChapterChanged={onChapterChanged}
        />
      ))}
    </div>
  );
}

function MatchCard({ idx, match, suggestions, editors, isOwner, canMutate, busy,
                     onRewrite, onAssign, onAccept, onDecline, reload }) {
  const [editorId, setEditorId] = useState('');
  const openSugs = suggestions.filter((s) => !['declined', 'accepted'].includes(s.status));

  return (
    <div
      data-testid={`plagiarism-match-${idx}`}
      className="rounded-md border border-border bg-background/60 p-2.5"
    >
      <div className="flex items-start gap-2">
        <div className={cn(
          'rounded-sm px-1.5 py-0.5 text-[13px] font-mono-stat',
          match.similarity > 60 ? 'bg-destructive/15 text-destructive'
            : match.similarity > 25 ? 'bg-warning/20 text-warning-foreground'
              : 'bg-muted text-muted-foreground'
        )}>
          {Number(match.similarity || 0).toFixed(1)}%
        </div>
        <div className="flex-1 min-w-0">
          <div className="truncate text-[13.5px] font-medium">
            {match.source_title || match.source_url || '(unknown source)'}
          </div>
          {match.source_url && (
            <a
              href={match.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-[13px] text-primary hover:underline"
              data-testid={`plagiarism-match-source-${idx}`}
            >
              <ExternalLink className="h-2.5 w-2.5" /> {match.source_url.slice(0, 48)}
            </a>
          )}
        </div>
      </div>

      <blockquote className="mt-2 rounded-sm border-l-2 border-destructive/60 bg-destructive/5 px-2 py-1 font-serif text-[14px] italic text-foreground/90">
        {match.quote || '(no excerpt returned)'}
      </blockquote>

      {/* Toolbar */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          disabled={busy || !canMutate}
          onClick={() => onRewrite(idx)}
          className="h-7 gap-1 px-2 text-[13px] text-primary disabled:opacity-50"
          data-testid={`plagiarism-rewrite-${idx}`}
          title="Generate an AI rewrite — costs Claude Sonnet tokens"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
          AI rewrite
        </Button>
        <div className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1">
          <UserPlus className="h-3 w-3 text-muted-foreground" />
          <Select value={editorId} onValueChange={setEditorId}>
            <SelectTrigger className="h-6 w-[120px] border-0 text-[13px] focus:ring-0" data-testid={`plagiarism-editor-select-${idx}`}>
              <SelectValue placeholder="Editor…" />
            </SelectTrigger>
            <SelectContent>
              {editors.length === 0 && (
                <SelectItem value="__empty" disabled>(no editors on this project)</SelectItem>
              )}
              {editors.map((e) => (
                <SelectItem key={e.user_id} value={e.user_id}>
                  {e.name || e.email} {e.role === 'owner' ? ' (owner)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy || !editorId || editorId === '__empty' || !canMutate}
            onClick={() => onAssign(idx, editorId)}
            className="h-6 px-1.5 text-[13px] text-primary"
            data-testid={`plagiarism-assign-${idx}`}
          >
            Assign
          </Button>
        </div>
      </div>

      {/* Suggestions (AI or editor) */}
      {openSugs.map((s) => (
        <SuggestionRow
          key={s.id}
          sug={s}
          isOwner={isOwner}
          onAccept={() => onAccept(s.id)}
          onDecline={() => onDecline(s.id)}
          reload={reload}
        />
      ))}
    </div>
  );
}

function SuggestionRow({ sug, isOwner, onAccept, onDecline, reload }) {
  const isAi = sug.kind === 'ai_rewrite';
  const isEditor = sug.kind === 'editor_review';
  const [editing, setEditing] = useState(false);
  const [editorBody, setEditorBody] = useState(sug.proposed_text || sug.match_quote || '');
  const [saving, setSaving] = useState(false);

  // Editor user picking up an assigned task: they can submit a new edit.
  const isAssignedToMe = sug.assigned_to && !isOwner;
  const allowEditorSubmit = (isEditor || (isAi && isAssignedToMe)) &&
                            ['open', 'editor_review'].includes(sug.status);

  const submitEdit = async () => {
    setSaving(true);
    try {
      await plagiarismApi.editorEdit(sug.id, editorBody);
      toast.success('Edit submitted — author will review.');
      setEditing(false);
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed.');
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid={`plagiarism-suggestion-${sug.id}`}
      className="mt-2 rounded-sm border border-success/30 bg-success/5 p-2"
    >
      <div className="flex items-center gap-1.5 text-[13px] font-semibold">
        {isAi ? <Wand2 className="h-3 w-3 text-success" /> : <UserPlus className="h-3 w-3 text-success" />}
        {isAi ? 'AI rewrite' : `Editor: ${sug.assigned_to || 'unassigned'}`}
        {isAi && Number(sug.tokens_used) > 0 && (
          <span
            className="rounded-sm bg-primary/10 px-1 font-mono-stat text-[13px] text-primary"
            title="Tokens debited from your wallet for this rewrite"
            data-testid={`plagiarism-suggestion-cost-${sug.id}`}
          >
            −{sug.tokens_used} tok
          </span>
        )}
        <span className="ml-auto rounded-sm bg-muted/60 px-1 text-[13px] font-mono-stat text-muted-foreground">{sug.status}</span>
      </div>

      {editing ? (
        <Textarea
          value={editorBody}
          onChange={(e) => setEditorBody(e.target.value)}
          rows={4}
          className="mt-2 font-serif text-[13.5px]"
          data-testid={`plagiarism-suggestion-edit-${sug.id}`}
        />
      ) : (
        <div className="mt-1 font-serif text-[13.5px] text-foreground/90 whitespace-pre-wrap">
          {sug.proposed_text || <span className="italic text-muted-foreground">Awaiting editor input…</span>}
        </div>
      )}

      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {isOwner && sug.proposed_text && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onAccept}
              className="h-6 gap-1 px-1.5 text-[13px] text-success"
              data-testid={`plagiarism-accept-${sug.id}`}
            >
              <Check className="h-3 w-3" /> Accept
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDecline}
              className="h-6 gap-1 px-1.5 text-[13px] text-destructive"
              data-testid={`plagiarism-decline-${sug.id}`}
            >
              <XCircle className="h-3 w-3" /> Decline
            </Button>
          </>
        )}

        {allowEditorSubmit && (
          editing ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={submitEdit}
                disabled={saving || !editorBody.trim()}
                className="h-6 gap-1 px-1.5 text-[13px] text-primary"
                data-testid={`plagiarism-submit-edit-${sug.id}`}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Submit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(false)}
                className="h-6 px-1.5 text-[13px]"
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditing(true)}
              className="h-6 gap-1 px-1.5 text-[13px] text-primary"
              data-testid={`plagiarism-edit-as-editor-${sug.id}`}
            >
              <Wand2 className="h-3 w-3" /> Edit as editor
            </Button>
          )
        )}
      </div>
    </div>
  );
}
